import type { SimpleIcon } from 'simple-icons';
import {
  siInstagram,
  siFacebook,
  siThreads,
  siTiktok,
  siX,
  siSnapchat,
  siTelegram,
  siBluesky,
  siReddit,
  siPinterest,
} from 'simple-icons';

/** Stable order matching Simple Icons / common layout (unknown platforms sort last). */
const PLATFORM_ORDER = [
  'instagram',
  'facebook',
  'threads',
  'tiktok',
  'x',
  'twitter',
  'snapchat',
  'telegram',
  'bluesky',
  'reddit',
  'pinterest',
] as const;

const PLATFORM_ICONS: Record<string, SimpleIcon> = {
  instagram: siInstagram,
  facebook: siFacebook,
  threads: siThreads,
  tiktok: siTiktok,
  x: siX,
  twitter: siX,
  snapchat: siSnapchat,
  telegram: siTelegram,
  bluesky: siBluesky,
  reddit: siReddit,
  pinterest: siPinterest,
};

export function getSocialSimpleIcon(platform: string): SimpleIcon | null {
  const key = platform.trim().toLowerCase();
  return PLATFORM_ICONS[key] ?? null;
}

export function socialProfileUrl(platform: string, value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;

  const raw = v.replace(/^@+/, '');
  const key = platform.trim().toLowerCase();

  switch (key) {
    case 'instagram':
      return `https://www.instagram.com/${encodeURIComponent(raw)}/`;
    case 'facebook':
      return `https://www.facebook.com/${encodeURIComponent(raw)}`;
    case 'threads':
      return `https://www.threads.net/@${encodeURIComponent(raw)}/`;
    case 'tiktok':
      return `https://www.tiktok.com/@${encodeURIComponent(raw)}`;
    case 'x':
    case 'twitter':
      return `https://x.com/${encodeURIComponent(raw)}`;
    case 'snapchat':
      return `https://www.snapchat.com/add/${encodeURIComponent(raw)}`;
    case 'telegram':
      return `https://t.me/${encodeURIComponent(raw)}`;
    case 'bluesky':
      return `https://bsky.app/profile/${encodeURIComponent(raw)}`;
    case 'reddit':
      return `https://www.reddit.com/user/${encodeURIComponent(raw)}/`;
    case 'pinterest':
      return `https://www.pinterest.com/${encodeURIComponent(raw)}/`;
    default:
      return null;
  }
}

export function sortListingSocials<T extends { platform: string }>(items: T[]): T[] {
  const rank = (p: string) => {
    const i = PLATFORM_ORDER.indexOf(p.trim().toLowerCase() as (typeof PLATFORM_ORDER)[number]);
    return i === -1 ? PLATFORM_ORDER.length : i;
  };
  return [...items].sort((a, b) => rank(a.platform) - rank(b.platform));
}

type SocialEntry = { platform: string; value: string };

/**
 * Accepts either:
 * - array format: [{ platform: "instagram", value: "@name" }]
 * - firestore map format: { instagram_handle: "@name", tiktok_handle: "@name" }
 */
export function normalizeListingSocials(input: unknown): SocialEntry[] {
  if (Array.isArray(input)) {
    return input
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const platform = String((item as { platform?: unknown }).platform ?? '').trim();
        const value = String((item as { value?: unknown }).value ?? '').trim();
        if (!platform || !value) return null;
        return { platform, value };
      })
      .filter((item): item is SocialEntry => item != null);
  }

  if (!input || typeof input !== 'object') return [];

  return Object.entries(input as Record<string, unknown>)
    .map(([key, raw]) => {
      if (typeof raw !== 'string') return null;
      const value = raw.trim();
      if (!value) return null;
      if (!key.endsWith('_handle')) return null;
      const platform = key.slice(0, -'_handle'.length).trim();
      if (!platform) return null;
      return { platform, value };
    })
    .filter((item): item is SocialEntry => item != null);
}
